// Egységes tipográfia-primitív — iparági best practice: 1 font (Outfit), 3 súly (400/600 + a badge külön),
// kompakt 4-méretes skála (11 · 12 · 13 · 16), 3-lépcsős nyomaték-rámpa témánként. A színek a SZEMANTIKUS tokenekből jönnek
// (`text-foreground`=primary / `text-muted-foreground`=secondary / `text-subtle`=tertiary / `text-brand`),
// amelyek témánként külön értéket kapnak (:root light / .theme-dark) → a <Text> automatikusan a helyes színt
// adja sötét ÉS világos módban, külön téma-elágazás nélkül. A badge/ikon KÜLÖN szín-rendszer.
//
// SZÍN-POLITIKA (fix szerep): primary=cím+body · secondary=label/fejléc/meta/supporting · tertiary CSAK
// placeholder/disabled/dekoratív (nem „muszáj-elolvasni" szöveg → sose tertiary, mert bukja az AA-kontrasztot).
// Méret-tokenek (tailwind.config): xs=11, sm=12, md=13, lg=16. (A `base`=14 primitív marad, de a <Text> NEM használja.)
import { cva, type VariantProps } from 'class-variance-authority';
import { createElement, type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

// A `tone` a `variant` alap-színét ÍRJA FELÜL (a tone-osztályok a variant után jönnek → twMerge-ben ők nyernek).
const textVariants = cva('', {
  variants: {
    variant: {
      title: 'text-foreground text-lg font-semibold', //     16 / 600 / primary — cím (kártya/modál/oldal)
      subtitle: 'text-md text-muted-foreground font-normal', // 13 / 400 / secondary — cím alatti supporting
      itemTitle: 'text-md text-foreground font-medium', //    13 / 500 / primary — lista-anchor (azonosító oszlop), medium
      body: 'text-md text-foreground font-normal', //         13 / 400 / primary — alap olvasható szöveg
      secondary: 'text-muted-foreground text-sm font-normal', // 12 / 400 / secondary — meta, caption, timestamp
      overline: 'text-muted-foreground text-xs font-semibold uppercase tracking-[0.07em]', // 11 / 600 / secondary (muted, AA) + CAPS — mikro-címke, fejléc
    },
    tone: {
      primary: 'text-foreground',
      secondary: 'text-muted-foreground',
      // tertiary = leg-de-emphasizált tier: halvány szín + LIGHT (300) súly (szándékos rendszer-szabály).
      tertiary: 'text-subtle font-light',
      brand: 'text-brand',
    },
  },
  defaultVariants: { variant: 'body' },
});

type TextElement = 'span' | 'p' | 'div' | 'h1' | 'h2' | 'h3' | 'label' | 'dt' | 'dd';

export interface TextProps extends HTMLAttributes<HTMLElement>, VariantProps<typeof textVariants> {
  /** Polimorf elem — alap: `span`. Egyszerű `createElement` (nincs Radix Slot). */
  as?: TextElement;
}

/** Tipográfia-primitív. `variant` adja a méret/súly/szín-hármast; `tone` opcionálisan felülírja a színt
 *  (interaktív/aktív szöveghez a `brand`). A `className` (pl. `truncate`, igazítás) rátehető. */
export function Text({ as = 'span', variant, tone, className, ...props }: TextProps): JSX.Element {
  return createElement(as, {
    className: cn(textVariants({ variant, tone }), className),
    ...props,
  });
}

export { textVariants };
